import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { haySesion } from "@/lib/sesionTokens";

// Extraída como función nombrada y exportada (en vez de un inline en el
// config de persist) para poder testearla de forma aislada, sin depender de
// la mecánica de rehidratación de zustand/persist — ver
// src/_zustand/auth.migracion.test.js.
export const migrateAuthStorage = (persistedState, version) => {
  if (version < 1 && persistedState?.authType === 'user') {
    let sesionActiva = false;
    try {
      sesionActiva = haySesion();
    } catch {
      sesionActiva = false;
    }

    if (!sesionActiva) {
      return {
        ...persistedState,
        user: {},
        pin: null,
        statusAuth: 'pending',
        authType: 'invitado',
      };
    }
  }
  return persistedState;
};

export const useAuth = create()(
  persist(
    (set) => ({
      pin: null,
      statusAuth: 'pending',
      tempCard: {},
      cards: [],
      authType: 'invitado',
      user: {},
      userAddressName: '',
      userAddressTag: 'Ubicación actual',
      userAddress: [],
      setUserAuth: (data = {}, type = 'invitado') => {
        set(() => {
          return {
            user: data,
            statusAuth: 'valid',
            authType: type,
          };
        });
      },
      // Único camino a `authed` para una sesión JWT real (CA-22/CA-32.1):
      // usuario con accessToken/refreshToken emitidos por loginUsuario/
      // loginGoogle/loginApple (ver src/lib/sesionTokens.js). `pin` queda en
      // null a propósito — es exclusivo del invitado verificado por PIN real
      // (setPin, usado por FormUser.jsx, CA-32.2) y no debe confundirse con
      // este camino. `authed` deriva de tener el token guardado, nunca de
      // comparar `exp` contra el reloj local (CA-35).
      setSesionAuth: (data = {}) => {
        set(() => {
          return {
            user: data,
            statusAuth: 'authed',
            authType: 'user',
            pin: null,
          };
        });
      },
      setUserAddress: (data) => {
        set(() => {
          return {
            userAddress: data
          };
        });
      },
      setUserAddressName: (data) => {
        set(() => {
          return {
            userAddressName: data
          };
        });
      },
      setUserAddressTag: (data) => {
        set(() => {
          return {
            userAddressTag: data
          };
        });
      },
      setPin: (data) => {
        set(() => {
          return {
            pin: data,
            statusAuth: 'authed',
          };
        });
      },
      // PA-14: cualquier logout (incluido el forzado por un refresh de sesión
      // fallido, ver useSesionExpirada.js) debe borrar tarjetas OneClick y
      // direcciones de un usuario real — dejarlas visibles tras cerrar sesión
      // es un problema de privacidad, no solo de estado inconsistente.
      clearuserAuth: () => {
        set(() => {
          return {
            pin: null,
            statusAuth: 'pending',
            tempCard: {},
            cards: [],
            authType: 'invitado',
            user: {},
            userAddress: [],
            userAddressName: '',
            userAddressTag: 'Ubicación actual',
          };
        });
      },
      incrementTotalPedidos: () => {
        set((state) => ({
          user: {
            ...state.user,
            total_pedidos: (state.user.total_pedidos ?? 0) + 1,
          },
        }));
      },
      setTempCard: (data) => {
        set(() => {
          return {
            tempCard: data,
          };
        });
      },
      setCards: (data) => {
        set((state) => ({
          cards: typeof data === 'function' ? data(state.cards) : data,
        }));
      },
    }),
    {
      name: "auth-storage",// name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage),// 'localStorage','sessionStorage' is used
      version: 1,
      // Caso borde 4 / R12 (migración de login JWT): un `auth-storage` de
      // antes de esta migración puede tener authType:'user' + statusAuth:
      // 'authed' con el centinela pin:"123456" y SIN ningún token de sesión
      // real — ese usuario mandaría el token invitado con enforcement de
      // identidad encendido y recibiría 403 en cada request. Se degrada a
      // invitado una sola vez, al primer arranque tras el deploy. Los blobs
      // con authType:'invitado' (invitado verificado por PIN a mitad de
      // checkout, ver FormUser.jsx) quedan intactos: degradarlos sería
      // exactamente la regresión que CA-32 prohíbe.
      migrate: migrateAuthStorage,
    }
  )
);